import { Box, Grid, TextField } from '@mui/material';
import * as React from 'react';

import { ContextStore } from '../store/contextStore';
import { loadGoogleMaps } from '../utils/googleMaps';
import { isValidLat, isValidLng } from '../utils/validators/coordinates';

/**
 * Rendering modes for LatLngPicker.
 *
 * - TEXT_AND_MAP: show both text fields and the map (default)
 * - TEXT: show only text fields
 * - MAP: show only the map
 */
export const TEXT_AND_MAP = 'TEXT_AND_MAP';
export const TEXT = 'TEXT';
export const MAP = 'MAP';

const DEFAULT_CENTER = { lat: 0, lng: 0 };
const DEFAULT_ZOOM = 2;
const PLACED_ZOOM = 15;

/**
 * A reusable coordinate picker with optional Google Map.
 *
 * Props:
 *   latitude      - initial latitude value (number, string, or empty)
 *   longitude     - initial longitude value (number, string, or empty)
 *   onUpdate      - called with { latitude: number|null, longitude: number|null }
 *                   when valid coordinates are entered or the map is clicked
 *   mode          - TEXT_AND_MAP (default), TEXT, or MAP
 *   mapHeight     - map height in px (default: 250)
 *   disabled      - disable inputs + map interaction
 *   textFieldProps - extra props passed to both TextFields (e.g. InputLabelProps)
 */
const LatLngPicker = ({
    latitude: latProp,
    longitude: lngProp,
    onUpdate,
    mode = TEXT_AND_MAP,
    mapHeight = 250,
    disabled = false,
    textFieldProps = {},
}) => {
    const { settings } = React.useContext(ContextStore);
    const googleApiKey = settings?.google_maps_key;

    const [lat, setLat] = React.useState(latProp ?? '');
    const [lng, setLng] = React.useState(lngProp ?? '');
    const [latError, setLatError] = React.useState(false);
    const [lngError, setLngError] = React.useState(false);

    // Google Map instance + marker, managed via refs to avoid re-renders
    const mapRef = React.useRef(null);
    const markerRef = React.useRef(null);
    const mapElRef = React.useRef(null);
    const [apiReady, setApiReady] = React.useState(false);

    // Sync from parent when props change (e.g. loading existing data)
    React.useEffect(() => {
        setLat(latProp ?? '');
        setLng(lngProp ?? '');
        setLatError(false);
        setLngError(false);
    }, [latProp, lngProp]);

    // Load the Google Maps API if needed
    const showMap = mode !== TEXT;
    React.useEffect(() => {
        if (!showMap || !googleApiKey) return;
        loadGoogleMaps(googleApiKey)
            .then(() => setApiReady(true))
            .catch((e) => console.warn('LatLngPicker: Google Maps failed to load', e));
    }, [showMap, googleApiKey]);

    // Create the map instance once the API is ready and the DOM element exists
    React.useEffect(() => {
        if (!apiReady || !window.google?.maps || !mapElRef.current || mapRef.current) return;
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        const hasPosition = !Number.isNaN(latNum) && !Number.isNaN(lngNum)
            && isValidLat(lat) && isValidLng(lng);
        const center = hasPosition ? { lat: latNum, lng: lngNum } : DEFAULT_CENTER;
        const zoom = hasPosition ? PLACED_ZOOM : DEFAULT_ZOOM;

        const map = new window.google.maps.Map(mapElRef.current, {
            center,
            zoom,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
        });
        mapRef.current = map;

        if (hasPosition) {
            markerRef.current = new window.google.maps.Marker({ position: center, map });
        }

        map.addListener('click', (e) => {
            if (disabled) return;
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();
            setLat(String(newLat));
            setLng(String(newLng));
            setLatError(false);
            setLngError(false);
            if (onUpdate) onUpdate({ latitude: newLat, longitude: newLng });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiReady]);

    // Update marker + pan when lat/lng state changes
    React.useEffect(() => {
        if (!mapRef.current) return;
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        const hasPosition = !Number.isNaN(latNum) && !Number.isNaN(lngNum)
            && isValidLat(lat) && isValidLng(lng);

        if (hasPosition) {
            const pos = { lat: latNum, lng: lngNum };
            if (markerRef.current) {
                markerRef.current.setPosition(pos);
            } else {
                markerRef.current = new window.google.maps.Marker({ position: pos, map: mapRef.current });
            }
            mapRef.current.panTo(pos);
            if (mapRef.current.getZoom() < PLACED_ZOOM) {
                mapRef.current.setZoom(PLACED_ZOOM);
            }
        } else if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
        }
    }, [lat, lng]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }
            mapRef.current = null;
        };
    }, []);

    const validate = (latVal, lngVal) => {
        const latEmpty = latVal === '' || latVal == null;
        const lngEmpty = lngVal === '' || lngVal == null;

        if (latEmpty && lngEmpty) {
            setLatError(false);
            setLngError(false);
            return true;
        }

        const latOk = !latEmpty && isValidLat(latVal);
        const lngOk = !lngEmpty && isValidLng(lngVal);

        setLatError(!latOk);
        setLngError(!lngOk);

        return latOk && lngOk;
    };

    const handleLatChange = (e) => {
        const val = e.target.value;
        setLat(val);
        if (validate(val, lng) && onUpdate) {
            const latEmpty = val === '' || val == null;
            const lngEmpty = lng === '' || lng == null;
            onUpdate({
                latitude: latEmpty ? null : parseFloat(val),
                longitude: lngEmpty ? null : parseFloat(lng),
            });
        }
    };

    const handleLngChange = (e) => {
        const val = e.target.value;
        setLng(val);
        if (validate(lat, val) && onUpdate) {
            const latEmpty = lat === '' || lat == null;
            const lngEmpty = val === '' || val == null;
            onUpdate({
                latitude: latEmpty ? null : parseFloat(lat),
                longitude: lngEmpty ? null : parseFloat(val),
            });
        }
    };

    const showText = mode !== MAP;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {showText && (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            variant="filled"
                            label="Latitude"
                            type="number"
                            value={lat}
                            onChange={handleLatChange}
                            fullWidth
                            size="small"
                            disabled={disabled}
                            error={latError}
                            helperText={latError ? '-90 to 90' : undefined}
                            {...textFieldProps}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            variant="filled"
                            label="Longitude"
                            type="number"
                            value={lng}
                            onChange={handleLngChange}
                            fullWidth
                            size="small"
                            disabled={disabled}
                            error={lngError}
                            helperText={lngError ? '-180 to 180' : undefined}
                            {...textFieldProps}
                        />
                    </Grid>
                </Grid>
            )}

            {showMap && googleApiKey && (
                <div
                    ref={mapElRef}
                    style={{ width: '100%', height: mapHeight, borderRadius: 8 }}
                />
            )}

            {showMap && !googleApiKey && (
                <Box
                    sx={{
                        width: '100%',
                        height: mapHeight,
                        borderRadius: 2,
                        bgcolor: '#e8e8e8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <span style={{ color: '#666' }}>Google Maps API key is not configured</span>
                </Box>
            )}
        </Box>
    );
};

export default LatLngPicker;
